import { useState } from 'react'
import { lumi } from '@/lib/lumi'

export function usePrints() {
  const [prints, setPrints] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  const fetchPrints = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.prints.list({
        sort: { createdAt: -1 }
      })
      setPrints(list || [])
    } catch (error) {
      console.error('Erro ao buscar prints:', error)
    } finally {
      setLoading(false)
    }
  }

  const uploadMultiplePrints = async (imageFiles: File[]) => {
    setUploading(true)
    try {
      // Upload de múltiplas imagens de uma vez
      const uploadResults = await lumi.tools.file.upload(imageFiles)

      console.log('Upload results:', uploadResults)

      // Verificar se houve erro
      if (!uploadResults || uploadResults.length === 0) {
        throw new Error('Nenhum resultado retornado do upload')
      }

      // Filtrar apenas uploads bem-sucedidos
      const successfulUploads = uploadResults.filter((result: any) => result.fileUrl && !result.uploadError)

      if (successfulUploads.length === 0) {
        const errors = uploadResults.filter((r: any) => r.uploadError).map((r: any) => r.uploadError).join(', ')
        throw new Error(errors || 'Nenhuma imagem foi enviada com sucesso')
      }

      // Salvar cada imagem no banco
      const savePromises = successfulUploads.map((result: any) => 
        lumi.entities.prints.create({
          imageUrl: result.fileUrl,
          creator: 'admin',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
      )

      await Promise.all(savePromises)

      await fetchPrints()
      return { 
        success: true, 
        message: `${successfulUploads.length} print(s) enviado(s) com sucesso!`,
        uploaded: successfulUploads.length,
        failed: uploadResults.length - successfulUploads.length
      }
    } catch (error: any) {
      console.error('Erro ao enviar prints:', error)
      const errorMessage = error?.data?.error || error?.message || 'Erro ao enviar prints. Tente novamente.'
      return { success: false, message: errorMessage, uploaded: 0, failed: imageFiles.length }
    } finally {
      setUploading(false)
    }
  }

  const removeDuplicates = async () => {
    try {
      const { list } = await lumi.entities.prints.list({})
      const urlMap = new Map<string, any[]>()
      
      // Agrupar por imageUrl
      list.forEach((print: any) => {
        const url = print.imageUrl
        if (!urlMap.has(url)) {
          urlMap.set(url, [])
        }
        urlMap.get(url)!.push(print)
      })
      
      // Remover duplicados (manter o mais antigo)
      const deletePromises: Promise<any>[] = []
      urlMap.forEach((prints) => {
        if (prints.length > 1) {
          // Ordenar por data de criação e manter o primeiro
          prints.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
          // Remover todos exceto o primeiro
          for (let i = 1; i < prints.length; i++) {
            deletePromises.push(lumi.entities.prints.delete(prints[i]._id))
          }
        }
      })
      
      if (deletePromises.length > 0) {
        await Promise.all(deletePromises)
        await fetchPrints()
        return { success: true, removed: deletePromises.length }
      }
      
      return { success: true, removed: 0 }
    } catch (error) {
      console.error('Erro ao remover duplicados:', error)
      return { success: false, removed: 0 }
    }
  }

  const deletePrint = async (printId: string) => {
    try {
      await lumi.entities.prints.delete(printId)
      await fetchPrints()
      return { success: true, message: 'Print deletado com sucesso!' }
    } catch (error) {
      console.error('Erro ao deletar print:', error)
      return { success: false, message: 'Erro ao deletar print' }
    }
  }

  return { prints, loading, uploading, fetchPrints, uploadMultiplePrints, removeDuplicates, deletePrint }
}
